import { useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  isTokenExpired,
  getTokenRemainingTime,
  updateLastActivityTime,
  getLastActivityTime,
  logoutUser,
  logoutSeller,
  logoutCentre,
  logoutAdmin,
  isUserLoggedIn,
  isSellerLoggedIn,
  isCentreLoggedIn,
  isAdminLoggedIn,
} from "../utils/authSession";

// 7 Days Auto-Logout Timeout
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
const SELLER_SESSION_TIMEOUT_MS = SEVEN_DAYS_MS;
const USER_SESSION_TIMEOUT_MS = SEVEN_DAYS_MS;

// Throttle activity updates to once per 10 seconds
const ACTIVITY_THROTTLE_MS = 10 * 1000;
// Check interval for timers (10 seconds)
const CHECK_INTERVAL_MS = 10 * 1000;

const AutoLogoutManager = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const lastThrottleRef = useRef(0);
  const userTimerRef = useRef(null);
  const sellerTimerRef = useRef(null);

  // Check if any role is currently logged in
  const hasActiveSession = useCallback(() => {
    return (
      Boolean(localStorage.getItem("userToken")) ||
      Boolean(localStorage.getItem("sellerToken")) ||
      Boolean(localStorage.getItem("centreToken")) ||
      Boolean(localStorage.getItem("adminToken")) ||
      Boolean(localStorage.getItem("token"))
    );
  }, []);

  // Update activity timestamp with throttling
  const handleUserActivity = useCallback(() => {
    const now = Date.now();
    if (now - lastThrottleRef.current > ACTIVITY_THROTTLE_MS) {
      lastThrottleRef.current = now;
      if (hasActiveSession()) {
        updateLastActivityTime();
      }
    }
  }, [hasActiveSession]);

  // Main check function for token expiry and session duration
  const checkSessions = useCallback(() => {
    const userToken =
      localStorage.getItem("userToken") ||
      (localStorage.getItem("role") === "patient" ? localStorage.getItem("token") : null);
    const sellerToken =
      localStorage.getItem("sellerToken") ||
      (localStorage.getItem("role") === "seller" ? localStorage.getItem("token") : null);
    const centreToken =
      localStorage.getItem("centreToken") ||
      (localStorage.getItem("role") === "centre" ? localStorage.getItem("token") : null);
    const adminToken = localStorage.getItem("adminToken");

    // 1. Check JWT Expiration for User (Patient)
    if (userToken && isTokenExpired(userToken)) {
      logoutUser({ reason: "expired", navigate });
      return;
    }

    // 2. Check JWT Expiration for Seller
    if (sellerToken && isTokenExpired(sellerToken)) {
      logoutSeller({ reason: "expired", navigate });
      return;
    }

    // 3. Check JWT Expiration for Centre
    if (centreToken && isTokenExpired(centreToken)) {
      logoutCentre({ reason: "expired", navigate });
      return;
    }

    // 4. Check JWT Expiration for Admin
    if (adminToken && isTokenExpired(adminToken)) {
      logoutAdmin({ reason: "expired", navigate });
      return;
    }

    // 5. 7-Day Session Duration Check
    if (hasActiveSession()) {
      const lastActive = getLastActivityTime();
      const sessionAge = Date.now() - lastActive;

      // Check Seller Session (7 days)
      if (sellerToken || localStorage.getItem("role") === "seller") {
        if (sessionAge >= SELLER_SESSION_TIMEOUT_MS) {
          logoutSeller({ reason: "expired", navigate });
          return;
        }
      }

      // Check User/Patient Session (7 days)
      if (userToken || localStorage.getItem("role") === "patient" || localStorage.getItem("role") === "user") {
        if (sessionAge >= USER_SESSION_TIMEOUT_MS) {
          logoutUser({ reason: "expired", navigate });
          return;
        }
      }

      // Check Centre Session (7 days)
      if (centreToken || localStorage.getItem("role") === "centre") {
        if (sessionAge >= USER_SESSION_TIMEOUT_MS) {
          logoutCentre({ reason: "expired", navigate });
          return;
        }
      }

      // Check Admin Session (7 days)
      if (adminToken || localStorage.getItem("role") === "admin") {
        if (sessionAge >= USER_SESSION_TIMEOUT_MS) {
          logoutAdmin({ reason: "expired", navigate });
          return;
        }
      }
    }
  }, [hasActiveSession, navigate]);

  // Setup exact setTimeout timers for remaining token lifetime
  const scheduleTokenTimers = useCallback(() => {
    if (userTimerRef.current) clearTimeout(userTimerRef.current);
    if (sellerTimerRef.current) clearTimeout(sellerTimerRef.current);

    const userToken = localStorage.getItem("userToken");
    if (userToken) {
      const remaining = getTokenRemainingTime(userToken);
      if (remaining !== null) {
        if (remaining <= 0) {
          logoutUser({ reason: "expired", navigate });
        } else {
          // Add maximum safe delay (32-bit int limit or remaining time)
          const delay = Math.min(remaining, 2147483647);
          userTimerRef.current = setTimeout(() => {
            logoutUser({ reason: "expired", navigate });
          }, delay);
        }
      }
    }

    const sellerToken = localStorage.getItem("sellerToken");
    if (sellerToken) {
      const remaining = getTokenRemainingTime(sellerToken);
      if (remaining !== null) {
        if (remaining <= 0) {
          logoutSeller({ reason: "expired", navigate });
        } else {
          const delay = Math.min(remaining, 2147483647);
          sellerTimerRef.current = setTimeout(() => {
            logoutSeller({ reason: "expired", navigate });
          }, delay);
        }
      }
    }
  }, [navigate]);

  // Listen to user interaction events across the window
  useEffect(() => {
    // Initial activity timestamp setup if session exists
    if (hasActiveSession()) {
      if (!localStorage.getItem("last_active_time")) {
        updateLastActivityTime();
      }
    }

    const events = ["mousedown", "mousemove", "keydown", "scroll", "touchstart", "click"];
    events.forEach((event) => {
      window.addEventListener(event, handleUserActivity, { passive: true });
    });

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleUserActivity);
      });
    };
  }, [handleUserActivity, hasActiveSession]);

  // Interval check every CHECK_INTERVAL_MS
  useEffect(() => {
    checkSessions();
    scheduleTokenTimers();

    const intervalId = setInterval(() => {
      checkSessions();
    }, CHECK_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
      if (userTimerRef.current) clearTimeout(userTimerRef.current);
      if (sellerTimerRef.current) clearTimeout(sellerTimerRef.current);
    };
  }, [checkSessions, scheduleTokenTimers]);

  // Re-check sessions on route change
  useEffect(() => {
    checkSessions();
    scheduleTokenTimers();
  }, [location.pathname, checkSessions, scheduleTokenTimers]);

  // Listen for storage events (cross-tab login/logout) and custom auth events
  useEffect(() => {
    const handleStorage = (e) => {
      if (
        e.key === "userToken" ||
        e.key === "sellerToken" ||
        e.key === "centreToken" ||
        e.key === "adminToken" ||
        e.key === "token" ||
        e.key === "role" ||
        e.key === null // localStorage.clear() triggers storage event with key=null
      ) {
        checkSessions();
        scheduleTokenTimers();
      }
    };

    const handleAuthChange = () => {
      updateLastActivityTime();
      checkSessions();
      scheduleTokenTimers();
    };

    window.addEventListener("storage", handleStorage);
    window.addEventListener("userLogin", handleAuthChange);
    window.addEventListener("auth:change", handleAuthChange);

    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("userLogin", handleAuthChange);
      window.removeEventListener("auth:change", handleAuthChange);
    };
  }, [checkSessions, scheduleTokenTimers]);

  // Invisible monitoring component
  return null;
};

export default AutoLogoutManager;
